import { useSuspenseQuery, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { CalendarPlus, Search } from "lucide-react";
import { useTranslation } from "react-i18next";

import i18n from "@/i18n";
import Button from "@/components/button";
import { RoleLink } from "@/components/role-link";
import { localeFor } from "@/utils/formatters";

import { useAuth } from "@/context/auth";
import {
  listBusinessOffers,
  listBusinessShifts,
} from "@/queries/api/businessShiftApi";
import {
  listWorkerOffers,
  listWorkerShifts,
} from "@/queries/api/workerShiftApi";
import { QueryKeys } from "@/queries/queryKeys";
import { useRole } from "@/utils/hooks/use-role";

import { OffersList } from "./-components/offers";
import { UpcomingShifts } from "./-components/upcoming-shifts";
import { PreviousShifts } from "./-components/previous-shifts";
import { UpcomingShiftsEmployer } from "./-components/upcoming-shifts-employer";
import { PendingReview } from "./-components/pending-review";
import { PreviousWorkers } from "./-components/previous-workers";
import { StarredWorkers } from "./-components/starred-workers";
import { DashboardV2 } from "./-components/v2/dashboard-v2";
import { WorkerDashboardV2 } from "./-components/worker-v2/worker-dashboard-v2";
import env from "@/utils/environment";
import {
  listUpcomingShifts,
  listPreviousWorkers,
} from "@/queries/api/employerDashboardApi";
import { listStarredWorkers } from "@/queries/api/starredWorkerApi";
import { BannerSlot, useBanners } from "@/components/banner";
import {
  markWorkerOnboardingComplete,
  markBusinessOnboardingComplete,
} from "@/queries/api/authApi";
import React from "react";
import type { Offer } from "@/schemas/offer";
import { shiftToCard } from "@/utils/shift-card";
import { getBusinessProfile } from "@/queries/api/businessApi";

const ROLES = ["worker", "employer"] as const;
type Role = (typeof ROLES)[number];

// The "mark onboarding complete" endpoints are a side-effecting sync (they tell
// Keycloak the user has finished onboarding when the BE detects all steps are done)
// and double as the read of current status. We only need to fire them once per
// session — refetching on mount/focus/reconnect was driving the 429s on dashboard.
function useOnboardingStatus(role: string) {
  return useQuery({
    queryKey: ["onboarding-status", role],
    queryFn: async () => {
      const res = role === "worker"
        ? await markWorkerOnboardingComplete()
        : await markBusinessOnboardingComplete();
      return res.success;
    },
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });
}

export function makeShiftsQuery(role: Role) {
  const today = new Date().toISOString(); // Full ISO string with time
  return {
    queryKey: [QueryKeys.DASHBOARD, QueryKeys.SHIFT_LIST, role],
    queryFn: async () => {
      if (role === "employer") {
        const response = await listBusinessShifts({
          startDate: today,
          pageSize: 50,
        });
        return response.assignments.map((assignment) => {
          const shift = assignment.shift;
          return shiftToCard(shift, {
            id: assignment.id,
            businessName: shift.business.name ?? "Business",
            workerId: assignment.worker_id,
            workerName: assignment.worker.full_name,
            dateLabel: assignment.start_date_time
              ? new Date(assignment.start_date_time).toLocaleDateString(
                  localeFor(i18n.language || "en"),
                  { month: "short", day: "numeric", year: "numeric" },
                )
              : "",
            timeLabel:
              assignment.start_date_time && assignment.end_date_time
                ? `${new Date(assignment.start_date_time).toLocaleTimeString(localeFor(i18n.language || "en"), { hour: "2-digit", minute: "2-digit", hour12: true })} – ${new Date(assignment.end_date_time).toLocaleTimeString(localeFor(i18n.language || "en"), { hour: "2-digit", minute: "2-digit", hour12: true })}`
                : "",
          });
        });
      } else {
        const response = await listWorkerShifts({ startDate: today });
        return response.assignments.map((assignment) => {
          const shift = assignment.shift;
          return shiftToCard(shift, {
            id: assignment.id,
            businessName: assignment.business?.name ?? "Business",
            workerId: assignment.worker_id,
            workerName: assignment.worker?.full_name,
            dateLabel: assignment.start_date_time
              ? new Date(assignment.start_date_time).toLocaleDateString(
                  localeFor(i18n.language || "en"),
                  { month: "short", day: "numeric", year: "numeric" },
                )
              : "",
            timeLabel:
              assignment.start_date_time && assignment.end_date_time
                ? `${new Date(assignment.start_date_time).toLocaleTimeString(localeFor(i18n.language || "en"), { hour: "2-digit", minute: "2-digit", hour12: true })} – ${new Date(assignment.end_date_time).toLocaleTimeString(localeFor(i18n.language || "en"), { hour: "2-digit", minute: "2-digit", hour12: true })}`
                : "",
          });
        });
      }
    },
  };
}

export function makeWorkerPreviousShiftsQuery() {
  const today = new Date().toISOString();
  return {
    queryKey: [QueryKeys.DASHBOARD, QueryKeys.SHIFT_LIST, "worker", "previous"],
    queryFn: async () => {
      const response = await listWorkerShifts({ endDate: today });
      const cards = response.assignments.map((assignment) => {
        const shift = assignment.shift;
        return shiftToCard(shift, {
          id: assignment.id,
          businessName: assignment.business?.name ?? "Business",
          workerId: assignment.worker_id,
          workerName: assignment.worker?.full_name,
          dateLabel: assignment.start_date_time
            ? new Date(assignment.start_date_time).toLocaleDateString(
                localeFor(i18n.language || "en"),
                { month: "short", day: "numeric", year: "numeric" },
              )
            : "",
          timeLabel:
            assignment.start_date_time && assignment.end_date_time
              ? `${new Date(assignment.start_date_time).toLocaleTimeString(localeFor(i18n.language || "en"), { hour: "2-digit", minute: "2-digit", hour12: true })} – ${new Date(assignment.end_date_time).toLocaleTimeString(localeFor(i18n.language || "en"), { hour: "2-digit", minute: "2-digit", hour12: true })}`
              : "",
        });
      });
      return cards.sort(
        (a, b) =>
          new Date(b.startDateTime ?? 0).getTime() -
          new Date(a.startDateTime ?? 0).getTime(),
      );
    },
  };
}

function makeOffersQuery(role: Role) {
  return {
    queryKey: [QueryKeys.OFFERS_LIST, role],
    queryFn: async () => {
      if (role === "worker") {
        const res = await listWorkerOffers();
        return { offers: res.offers, users: res.users || {} };
      } else {
        const res = await listBusinessOffers();
        return { offers: res.offers, users: res.users || {} };
      }
    },
  };
}

function makeBusinessProfileQuery(role: Role) {
  return {
    queryKey: [QueryKeys.BUSINESS_PROFILE],
    queryFn: getBusinessProfile,
    enabled: role === "employer",
  };
}

function makeEmployerUpcomingShiftsQuery() {
  return {
    queryKey: [QueryKeys.UPCOMING_SHIFTS],
    queryFn: () => listUpcomingShifts(28),
  };
}

function makePreviousWorkersQuery() {
  return {
    queryKey: [QueryKeys.PREVIOUS_WORKERS],
    queryFn: listPreviousWorkers,
  };
}

function makeStarredWorkersQuery() {
  return {
    queryKey: [QueryKeys.STARRED_WORKERS],
    queryFn: listStarredWorkers,
  };
}

export const Route = createFileRoute("/app/$role/_app/dashboard/")({
  component: DashboardPage,
  beforeLoad: ({ params }) => {
    if (!ROLES.includes(params.role as Role)) {
      throw new Error(`Invalid role: ${params.role}`);
    }
  },
  loader: ({ params, context }) => {
    const role = params.role as Role;
    context.queryClient.ensureQueryData(makeOffersQuery(role));
    if (role === "worker") {
      context.queryClient.ensureQueryData(makeShiftsQuery(role));
      context.queryClient.ensureQueryData(makeWorkerPreviousShiftsQuery());
    } else {
      context.queryClient.ensureQueryData(makeBusinessProfileQuery(role));
      context.queryClient.ensureQueryData(makeEmployerUpcomingShiftsQuery());
      context.queryClient.ensureQueryData(makePreviousWorkersQuery());
      context.queryClient.ensureQueryData(makeStarredWorkersQuery());
    }
  },
  staticData: {
    layout: {
      boxed: false,
    },
  },
});

function DashboardPage() {
  const { t } = useTranslation("dashboard");
  const { user } = useAuth();
  const role = useRole();
  const { data: offersData } = useSuspenseQuery(makeOffersQuery(role));
  const offers = offersData.offers;
  const users = offersData.users;
  const { data: businessProfile } = useQuery(makeBusinessProfileQuery(role));


  const banners = useBanners();

  const navigate = useNavigate();
  const { data: onboardingComplete } = useOnboardingStatus(role);

  React.useEffect(() => {
    if (user && onboardingComplete === false) {
      banners.show({
        kind: "warning",
        title: t("onboarding.title"),
        message: t("onboarding.message"),
        dismissible: true,
        action: {
          label: t("onboarding.completeSetup"),
          onClick: () => {
            navigate({ to: "/app/$role/setup", params: { role } });
          },
        },
      });
    }
  }, [user, role, banners, onboardingComplete, navigate, t]);

  return (
    <div
      className="flex flex-col w-full gap-3 bg-fog pb-12 md:pb-2 mx-auto px-4 lg:pl-8 py-9 max-w-[1320px]"
    >
      <BannerSlot />

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
        <div className="flex flex-col">
          <h2 className="heading-1">
            {t("welcome", { name: user?.firstName || t("defaultUser") })}
          </h2>
          {role === "employer" && (
            <p className="body-xs">{businessProfile?.business.name}</p>
          )}
          <p className="body-xs">{format(new Date(), "EEE, MMM dd, yyyy")}</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <Button
            variant="primary"
            className="w-full sm:w-[250px]"
            size={"lg"}
            asChild
          >
            <RoleLink
              to={
                role === "worker"
                  ? "/app/$role/search"
                  : "/app/$role/shifts/new"
              }
              className="no-underline!"
            >
              <span className="flex flex-row body! text-[16px]! justify-center w-full relative">
                <Search className="absolute -left-2" />
                {role === "worker" ? t("findShift") : t("createShift")}
              </span>
            </RoleLink>
          </Button>
          {role === "worker" && (
            <Button
              variant="primary"
              className="w-full sm:w-[250px]"
              size={"lg"}
              asChild
            >
              <RoleLink
                to="/app/$role/post-availability"
                className="no-underline!"
              >
                <span className="flex flex-row body! text-[16px]! justify-center w-full relative">
                  <CalendarPlus className="absolute -left-2" />
                  {t("postAvailability")}
                </span>
              </RoleLink>
            </Button>
          )}
        </div>
      </div>
      {role === "worker" ? (
        env.VITE_WORKER_DASHBOARD_V2 === "true" ? (
          <WorkerDashboardV2 />
        ) : (
          <WorkerDashboardGrid offers={offers} users={users} />
        )
      ) : env.VITE_EMPLOYER_DASHBOARD_V2 === "true" ? (
        <DashboardV2
          locationName={businessProfile?.business.name}
          locationId={String(businessProfile?.business.id ?? "default")}
        />
      ) : (
        <EmployerDashboardGrid offers={offers} users={users} />
      )}
    </div>
  );
}

function EmployerDashboardGrid({
  offers,
  users,
}: {
  offers: Array<Offer>;
  users: Record<string, any>;
}) {
  const { data: upcomingShifts } = useSuspenseQuery(
    makeEmployerUpcomingShiftsQuery(),
  );
  const { data: previousWorkers } = useSuspenseQuery(
    makePreviousWorkersQuery(),
  );
  const { data: starredWorkers } = useSuspenseQuery(makeStarredWorkersQuery());

  return (
    <div className="w-full overflow-x-auto lg:overflow-visible">
      <div className="grid gap-5 grid-cols-1 lg:grid-cols-2">
        <UpcomingShiftsEmployer items={upcomingShifts} />
        <PendingReview offers={offers} users={users} />
        <PreviousWorkers workers={previousWorkers} />
        <StarredWorkers workers={starredWorkers} />
      </div>
    </div>
  );
}

function WorkerDashboardGrid({
  offers,
  users,
}: {
  offers: Array<Offer>;
  users: Record<string, any>;
}) {
  const { t } = useTranslation("dashboard");
  const role = useRole();
  const { data: shifts } = useSuspenseQuery(makeShiftsQuery(role));
  const { data: previousShifts } = useSuspenseQuery(
    makeWorkerPreviousShiftsQuery(),
  );
  return (
    <div className="w-full overflow-x-auto lg:overflow-visible">
      <div className="grid gap-5 grid-cols-1 lg:grid-cols-2">
        <UpcomingShifts items={shifts} />
        <OffersList
          items={offers}
          users={users}
          title={t("newOffers.title")}
          emptyMessage={t("newOffers.empty")}
          filter={(o) => o.offeredBy === "business"}
        />
        <OffersList
          items={offers}
          users={users}
          title={t("pendingOffers.title")}
          emptyMessage={t("pendingOffers.empty")}
          filter={(o) => o.offeredBy === "worker"}
        />
        <PreviousShifts items={previousShifts} />
      </div>
    </div>
  );
}
